import { useEffect, useMemo, useState } from 'react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@g17eco/ui-modern/components/ui/dialog';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';

import { SessionFileTray } from './SessionFileTray';
import { useUploadSession } from '../hooks/useUploadSession';

interface LibraryUploadModalProps {
  isOpen: boolean;
  initiativeId: string;
  onClose: () => void;
}

export const LibraryUploadModal = ({ isOpen, initiativeId, onClose }: LibraryUploadModalProps) => {
  const uploadSession = useUploadSession({ initiativeId });
  const {
    session,
    isCreating,
    ensureSession,
    startFreshSession,
    commitSession,
    uploadFile,
    updateMetadata,
    removeFile,
    promoteSelected,
  } = uploadSession;

  // Reset failure must block the upload — a stale Add-Activity-flow session
  // would otherwise promote *its* files here under the library's empty-target.
  const [resetFailed, setResetFailed] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setResetFailed(false);
    startFreshSession().catch((error) => {
      setResetFailed(true);
      toastFromError({ error, title: 'Could not reset upload session' });
    });
  }, [isOpen, startFreshSession]);

  const unpromotedFiles = useMemo(
    () => (session?.files ?? []).filter((f) => !f.promotedToEvidenceId),
    [session],
  );
  const missingType = useMemo(
    () => unpromotedFiles.filter((f) => !f.metadata.evidenceType),
    [unpromotedFiles],
  );
  const hasMissingType = missingType.length > 0;

  const handleUpload = async (file: File, metadata?: Parameters<typeof uploadFile>[1]) => {
    await ensureSession();
    return uploadFile(file, metadata);
  };

  const handleConfirm = async () => {
    if (unpromotedFiles.length === 0) {
      onClose();
      return;
    }
    const fileIds = unpromotedFiles.map((f) => f.fileId);
    const { failures } = await promoteSelected(fileIds, { activityIds: [] });
    if (failures.length > 0) {
      toastFromError({
        error: failures[0].error,
        title: failures.length === fileIds.length
          ? 'Upload failed'
          : `Uploaded ${fileIds.length - failures.length}, failed ${failures.length}`,
      });
      return;
    }
    // Soft-fail commit; files are already promoted, just session bookkeeping.
    await commitSession().catch((error) => {
      toastFromError({ error, title: 'Upload succeeded but session commit failed' });
    });
    generateToast({
      color: 'success',
      title: `Uploaded ${fileIds.length} ${fileIds.length === 1 ? 'document' : 'documents'}`,
    });
    onClose();
  };

  const primaryLabel = unpromotedFiles.length === 0
    ? 'Done'
    : `Upload ${unpromotedFiles.length} ${unpromotedFiles.length === 1 ? 'document' : 'documents'}`;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className='tw:sm:max-w-2xl'
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Upload documents</DialogTitle>
          <DialogDescription>
            Drop invoices, meter readings, certificates, or other supporting documents into the
            library. You can link them to activities later.
          </DialogDescription>
        </DialogHeader>
        <SessionFileTray
          session={session}
          onUpload={handleUpload}
          onRemove={removeFile}
          onUpdateMetadata={updateMetadata}
          selectable={false}
          isCreatingSession={isCreating}
        />
        {resetFailed ? (
          <div className='tw:text-xs tw:text-ThemeAccentDanger'>
            Could not start a fresh upload session — close and reopen to retry.
          </div>
        ) : hasMissingType ? (
          <div className='tw:text-xs tw:text-ThemeAccentDanger'>
            Set a type on {missingType.length} file{missingType.length === 1 ? '' : 's'} before
            uploading.
          </div>
        ) : null}
        <DialogFooter>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={resetFailed || hasMissingType || isCreating}>
            {primaryLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
